import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../../zustand/ThemeStore';
import type { User } from '../../types/dashboard';

interface CoreMetricsProps {
  selectedUser: User | null;
  onBackClick: () => void;
}

export default function CoreMetrics({ selectedUser, onBackClick }: CoreMetricsProps) {
  const isDarkMode = useTheme((state) => state.theme);

  if (!selectedUser) return null;

  const getCumulativeData = () => {
    // If user has core_metrics, use them directly
    if (selectedUser.core_metrics) {
      return {
        representative: selectedUser.name,
        totalCalls: 0, // No call count available from core_metrics
        averageRepScore: 0, // No average score available from core_metrics
        repSubscores: {
          rapport_setting_expectations: selectedUser.core_metrics.rapport_setting_expectations || null,
          pain_extraction: selectedUser.core_metrics.pain_extraction || null,
          timeline_discovery: selectedUser.core_metrics.timeline_discovery || null,
          decision_maker_discovery: selectedUser.core_metrics.decision_maker_discovery || null,
          next_step_commitment: selectedUser.core_metrics.next_step_commitment || null
        }
      };
    }

    // Return structure with null values when no core_metrics available (will show N/A)
    return {
      representative: selectedUser.name,
      totalCalls: 0,
      averageRepScore: 0,
      repSubscores: {
        rapport_setting_expectations: null,
        pain_extraction: null,
        timeline_discovery: null,
        decision_maker_discovery: null,
        next_step_commitment: null
      }
    };
  };

  const cumulativeData = getCumulativeData();

  const getScoreColor = (score: number): string => {
    if (score >= 8) return '#1a9f6b';
    if (score >= 6) return '#fbbf24';
    return '#ef4444';
  };

  return (
    <div className="mb-10">
      {/* Back Button and User Info */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={onBackClick}
          className={`p-2 rounded-lg transition-all duration-300 hover:scale-110 ${
            isDarkMode 
              ? 'bg-[#0a1414] text-white border border-[#1a2424] hover:bg-[#0f1919]' 
              : 'bg-white text-[#053333] border border-gray-200 hover:bg-gray-50 shadow-sm'
          }`}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full border-3 border-[#1a9f6b] bg-[#1a9f6b] text-white flex items-center justify-center text-lg font-bold">
            {selectedUser?.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
          </div>
          <div>
            <h2 className={`text-2xl font-bold ${
              isDarkMode ? 'text-white' : 'text-[#053333]'
            }`}>
              {selectedUser?.name}
            </h2>
            <p className={`text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {selectedUser?.role}
            </p>
          </div>
        </div>
      </div>

      {/* Cumulative Rep Subscores - Always show cards */}
      {cumulativeData && (
        <div className="mb-8">
          <h3 className={`text-xl font-bold mb-6 ${
            isDarkMode ? 'text-white' : 'text-[#053333]'
          }`}>
            Core Metrics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { title: "Rapport Setting", value: cumulativeData.repSubscores.rapport_setting_expectations, key: "rapport" },
              { title: "Pain Extraction", value: cumulativeData.repSubscores.pain_extraction, key: "pain" },
              { title: "Timeline Discovery", value: cumulativeData.repSubscores.timeline_discovery, key: "timeline" },
              { title: "Decision Maker Discovery", value: cumulativeData.repSubscores.decision_maker_discovery, key: "decision" },
              { title: "Next Step Commitment", value: cumulativeData.repSubscores.next_step_commitment, key: "commitment" }
            ].map((card, index) => {
              const isValueAvailable = card.value !== null && card.value !== undefined;
              const score = isValueAvailable ? Math.min(Math.max(card.value || 0, 0), 10) : 0; // Ensure score is between 0-10
              const percentage = (score / 10) * 100;
              
              return (
                <div
                  key={index}
                  className={`rounded-xl p-6 transition-all duration-300 hover:scale-[1.02] ${
                    isDarkMode 
                      ? 'bg-[#0a1414] border border-[#1a2424]' 
                      : 'bg-white border border-gray-200 shadow-sm'
                  }`}
                >
                  <h3 className={`text-sm font-medium mb-4 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {card.title}
                  </h3>
                  <div className="mb-4">
                    {isValueAvailable ? (
                      <>
                        <span className={`text-3xl font-bold`} style={{ color: getScoreColor(score) }}> 
                          {score.toFixed(1)}
                        </span>
                        <span className={`text-sm ml-2 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          out of 10 ({Math.round(percentage)}%)
                        </span>
                      </>
                    ) : (
                      <span className={`text-3xl font-bold ${
                        isDarkMode ? 'text-gray-500' : 'text-gray-500'
                      }`}>
                        N/A
                      </span>
                    )}
                  </div>
                  {isValueAvailable ? (
                    <div className={`w-full rounded-full h-2 ${
                      isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-200'
                    }`}>
                      <div 
                        className={`h-2 rounded-full transition-all duration-500`}
                        style={{ 
                          width: `${percentage}%`,
                          backgroundColor: getScoreColor(score)
                        }}
                      />
                    </div>
                  ) : (
                    <div className={`w-full rounded-full h-2 ${
                      isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-200'
                    }`}>
                      {/* Empty progress bar for N/A values */}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
